import {
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { useShowDialog } from '@experiences/util';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApDropdown,
    ApDropdownItem,
    ApProgressSpinner,
} from '@uipath/portal-shell-react';
import type { ApDropdownCustomEvent } from '@uipath/portal-shell-types';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import useSWR from 'swr';

import type { Connector } from '../../../../services/connector/Connector.types';
import { getConnectors } from '../../../../services/connector/ConnectorService';
import type { ApiFlavor } from '../../../../services/llm-gateway/LlmGateway.types';
import { ApiFlavorToCustomConnectorTags } from '../../../../services/llm-gateway/LlmGateway.types';
import {
    LabelWithTooltip,
    type TooltipProps,
} from '../../subcomponents/LabelWithTooltip';
import { CreateCustomConnectorDialogBody } from './CreateCustomConnectorDialogBody';

interface ConnectorDropdownProps {
    organizationId: string;
    tenantId: string;
    label?: string;
    onChange: (newValue?: string, tags?: string) => void;
    value?: string;
    required?: boolean;
    disabled?: boolean;
    filter?: (connectorKey: string) => boolean;
    labelTooltip?: TooltipProps;
    apiFlavor?: ApiFlavor;
    restrictToCustomAiGateway?: boolean;
    supportCustomConnectors?: boolean;
}

const CREATE_CONNECTOR_VALUE = 'create-custom-connector';
const POLL_INTERVAL_MS = 2000;

export const ConnectorDropdown: React.FC<ConnectorDropdownProps> = ({
    organizationId, tenantId, label, onChange, value, required, disabled, filter, labelTooltip, apiFlavor, restrictToCustomAiGateway, supportCustomConnectors,
}) => {
    const { formatMessage: translate } = useIntl();
    const createDialog = useShowDialog();
    const [ creating, setCreating ] = useState(false);
    const [ localSelectedValue, setLocalSelectedValue ] = useState<string | undefined>(value);
    const intervalRef = useRef<number>();

    const {
        data, error, isValidating, mutate,
    } = useSWR(
        tenantId ? {
            url: 'integration-services-connectors',
            organizationId,
            tenantId,
        } : null,
        () => getConnectors(organizationId, tenantId),
    );

    useEffect(() => {
        setLocalSelectedValue(value);
    }, [ value ]);

    const hasMatchingCustomConnectorTag = useCallback((connector: Connector) => {
        if (!apiFlavor) {
            return false;
        }
        return ApiFlavorToCustomConnectorTags[apiFlavor].some(entry =>
            // `uipath-*` entries are catalog IS connector keys (not tags) — match against connector.key
            // because catalog connectors don't necessarily carry their own key in their `tags` CSV.
            // All other entries are tags — match against the connector's tags CSV.
            entry.startsWith('uipath-') ? connector.key === entry : (connector.tags?.includes(entry) ?? false)
        );
    }, [ apiFlavor ]);

    const hasAiGatewayTag = useCallback((connector: Connector) =>
        connector.tags?.includes('aigateway-') ?? false
    , []);

    const filteredData = useMemo(() => {
        if (restrictToCustomAiGateway) {
            return data?.filter(c => hasAiGatewayTag(c) && hasMatchingCustomConnectorTag(c)) ?? [];
        }
        return data?.filter(c => (!filter || filter(c.key)) || hasMatchingCustomConnectorTag(c)) ?? [];
    }, [ data, filter, hasMatchingCustomConnectorTag, hasAiGatewayTag, restrictToCustomAiGateway ]);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = undefined;
        }
        setCreating(false);
    }, []);

    const startPollingForNewConnectors = useCallback((newTab: Window) => {
        setCreating(true);

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        const oldConnectorKeys = new Set(data?.map(c => c.key) ?? []);

        intervalRef.current = window.setInterval(() => {
            mutate()
                .then((latestData) => {
                    const hasNewConnector = latestData?.some(c => !oldConnectorKeys.has(c.key));
                    if (hasNewConnector || newTab.closed) {
                        stopPolling();
                    }
                })
                .catch(stopPolling);
        }, POLL_INTERVAL_MS);
    }, [ data, mutate, stopPolling ]);

    // Use a ref to always have the latest callback available for the dialog
    const startPollingRef = useRef(startPollingForNewConnectors);
    startPollingRef.current = startPollingForNewConnectors;

    const openCreateCustomConnectorDialog = useCallback(async () => {
        await createDialog({
            title: '',
            customDialogContent: CreateCustomConnectorDialogBody,
            customDialogContentProps: {
                apiFlavor,
                restrictToCustomAiGateway,
                tenantId,
                onConnectorCreationStarted: (newTab: Window) => startPollingRef.current(newTab),
            },
            hideActions: true,
            unclosable: true,
        });
        setLocalSelectedValue(undefined);
    }, [ createDialog, apiFlavor, restrictToCustomAiGateway, tenantId ]);

    const onValueChanged = useCallback((event: ApDropdownCustomEvent<string | string[]>) => {
        const newValue = event.detail as string;
        setLocalSelectedValue(newValue);

        if (newValue === CREATE_CONNECTOR_VALUE) {
            if (supportCustomConnectors) {
                openCreateCustomConnectorDialog();
            }
            return;
        }
        const connector = data?.find(c => c.key === newValue);
        onChange(newValue, connector?.tags);
    }, [ supportCustomConnectors, data, onChange, openCreateCustomConnectorDialog ]);

    if (creating || (!data && isValidating)) {
        return (
            <UiStack direction="column">
                <UiText
                    style={{
                        marginTop: 8,
                        marginBottom: 8,
                    }}
                    variant={FontVariantToken.fontSizeMBold}
                    color="var(--color-foreground-de-emp)"
                >
                    {label}
                </UiText>
                <ApProgressSpinner />
            </UiStack>
        );
    }

    return (
        <UiStack direction='column'>
            <LabelWithTooltip
                label={label ?? ''}
                required={required}
                tooltip={labelTooltip ?? {
                    translationKey: 'CLIENT_BYO_LLM_TOOLTIP_CONNECTOR',
                    placement: 'top',
                }}
                dataCy="connector-dropdown-label-with-tooltip"
            />
            <ApDropdown
                style={{ width: '240px' }}
                selectedValue={localSelectedValue}
                data-cy="connector-dropdown"
                errorMessage={error?.message}
                required={required}
                disabled={disabled}
                onSelectedValueChanged={onValueChanged}
            >
                {supportCustomConnectors && <ApDropdownItem
                    key={CREATE_CONNECTOR_VALUE}
                    value={CREATE_CONNECTOR_VALUE}
                    label={'+ ' + translate({ id: 'CLIENT_BYO_LLM_CREATE_CUSTOM_CONNECTOR' })}
                />}
                {filteredData.map((item) => (
                    <ApDropdownItem
                        key={item.key}
                        value={item.key}
                        label={item.name}
                    />
                ))}
            </ApDropdown>
        </UiStack>
    );
};
